// src/Pages/Auth/OAuthFinish.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, setAuthToken } from "../../services/api";
import { toast } from "react-toastify";

function getParam(search, name) {
  const sp = new URLSearchParams(search);
  return sp.get(name);
}

// sallitaan token myös URL-fragmentista (#token=...)
function readTokenFromUrl(loc) {
  const qToken = getParam(loc.search, "token") || getParam(loc.search, "t") || getParam(loc.search, "jwt");
  if (qToken) return qToken;
  if (loc.hash) {
    const h = new URLSearchParams(loc.hash.replace(/^#/, ""));
    return h.get("token") || h.get("access_token") || h.get("jwt");
  }
  return null;
}

export default function OAuthFinish({ provider = "oauth" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh, user } = useAuth();

  const [state, setState] = useState({ phase: "starting", error: "" });

  const nextUrl = useMemo(() => {
    // salli backendin lähettää next-param
    const next = getParam(location.search, "next");
    return next && /^\/[A-Za-z0-9/_?&=%.-]*$/.test(next) ? next : null;
  }, [location.search]);

  useEffect(() => {
    (async () => {
      setState({ phase: "finishing", error: "" });

      try {
        // 1) Ota token talteen jos sellainen tuli
        const token = readTokenFromUrl(location);
        if (token) setAuthToken(token);

        // 2) Yritä lukea sessio (onnistuu myös jos backend laittoi httpOnly-keksin)
        try { await refresh?.(); } catch {}

        // 3) Jos ei vieläkään käyttäjää, kokeile suoraan /me
        let me = null;
        try {
          const res = await api.auth.me();
          me = res?.user || res || null;
        } catch {}

        const u = me || user;

        if (!u) {
          // Virhe: ei sessiota
          setState({ phase: "error", error: "Kirjautumista ei voitu viimeistellä. Yritä uudelleen." });
          toast.error("Google-kirjautuminen epäonnistui. Kokeile uudelleen.", { containerId: "global" });
          return;
        }

        // 4) Päätä mihin ohjataan
        // Jos profiili kesken → setup-wizard. Muuten dashboard tai next.
        const needsSetup =
          // liput joita backendi mahdollisesti käyttää
          u.onboarding_done === false ||
          u.onboarding_required === true ||
          u.profile_complete === false ||
          !u.display_name;

        if (needsSetup) {
          navigate("/onboarding", { replace: true });

          return;
        }

        // jos backend antoi next-paramin, käytä sitä – muuten dashboard
        navigate(nextUrl || "/dashboard", { replace: true });
      } catch (err) {
        console.error("OAuth finish error:", err);
        // tyypillinen tapaus: backend palautti err=409 tms.
        const errCode = getParam(location.search, "error") || getParam(location.search, "err") || "";
        if (errCode === "email_taken") {
          setState({
            phase: "error",
            error:
              "Sähköposti on jo käytössä toisella tilillä. Kirjaudu sisään sähköpostilla ja linkitä Google myöhemmin asetuksista.",
          });
          toast.info("Tämä sähköposti on jo rekisteröity. Kirjaudu sisään salasanalla.", { containerId: "global" });
          return;
        }
        setState({ phase: "error", error: "Kirjautumista ei voitu viimeistellä." });
        toast.error("Kirjautuminen epäonnistui.", { containerId: "global" });
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ajetaan kerran

  return (
    <div className="min-h-screen grid place-items-center bg-lemon/60 dark:bg-black/40 p-6">
      <div className="w-full max-w-md rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/75 dark:bg-black/60 p-6 text-center">
        {state.phase !== "error" ? (
          <>
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-black/20 border-t-black/80 dark:border-white/20 dark:border-t-white/80" />
            <p className="text-black/80 dark:text-white/80">
              Viimeistellään {provider === "google" ? "Google" : "OAuth"}-kirjautumista…
            </p>
          </>
        ) : (
          <>
            <p className="text-red-600 dark:text-red-400 font-semibold mb-3">{state.error}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => (window.location.href = api.auth.googleUrl())}
                className="px-4 py-2 rounded-lg ring-1 ring-black/10 dark:ring-white/10 bg-white dark:bg-black/50"
              >
                Yritä uudelleen
              </button>
              <button
                onClick={() => navigate("/kirjaudu")}
                className="px-4 py-2 rounded-lg bg-pink text-white dark:bg-primary"
              >
                Kirjaudu salasanalla
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
